import React from 'react';

import { AlertList } from '~components';
import { AlertMessage } from '~components/form-fields/inputs';

import { CutoverPricePlanResult, CutoverServiceTypeResult } from '~support-at-home/actions/sahServiceCutover';

import CutoverRunProgress from './CutoverRunProgress';

interface ComponentProps {
  onChange?: (name: string, value: any) => any;
  value: {
    applied_service_types?: CutoverServiceTypeResult;
    applied_price_plans?: CutoverPricePlanResult;
    /** The budget work runs in the background, so this step is handed a run to watch, not a result. */
    budget_run_id?: string;
    onRunningChange?: (isRunning: boolean) => void;
  };
}

/**
 * What the run actually did, step by step.
 *
 * Every step of the assistant is committed as it is confirmed, so this is a record of work already
 * done rather than a result that could still be rolled back. It is written that way deliberately -
 * a provider reading it needs to know what now stands, not what was attempted.
 */
const CutoverSummary: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { applied_service_types, applied_price_plans, budget_run_id, onRunningChange } = value ?? {};

  const done: string[] = [];

  if (applied_service_types) {
    const { created, skipped } = applied_service_types;

    // A step that did nothing at all says so plainly. "0 associations created against the
    // replacement service" reads like a failure rather than like a choice the provider made.
    if (created === 0 && skipped === 0) {
      done.push('No service type associations were created - none were selected for this step.');
    } else {
      const alsoSkipped = skipped > 0 ? `, ${skipped} already in place and left alone.` : '.';
      done.push(
        `${created} service type association${created === 1 ? '' : 's'} created against the replacement service${alsoSkipped}`,
      );
    }
  }

  if (applied_price_plans) {
    const { revisions, skipped } = applied_price_plans;

    if (revisions.length === 0 && skipped === 0) {
      done.push('No price plan revisions were added - no plans were selected for this step.');
    } else {
      const alsoSkipped =
        skipped > 0 ? `, ${skipped} plan${skipped === 1 ? '' : 's'} already carrying it and left alone.` : '.';
      done.push(
        `${revisions.length} price plan revision${revisions.length === 1 ? '' : 's'} added, priced from the services being replaced${alsoSkipped}`,
      );
    }
    if (revisions.length > 0) {
      done.push('To charge a different price for the replacement, edit the new revision as you would any other.');
    }
  }

  return (
    <div>
      {done.length === 0 ? (
        <AlertMessage
          id="cutover-summary-empty"
          name="cutover-summary-empty"
          value="Nothing was applied in this run."
          type="alert-info"
          color="info"
        />
      ) : (
        <AlertList header="This run has applied the following, and it now stands:" color="success" listItems={done} />
      )}

      {/* The budget work is a background run, so its outcome is watched rather than reported - it may
          still be going when this step is first shown. The running state is published to the form as
          well as to the modal, so the footer can take Close away while data is still changing. */}
      {budget_run_id && (
        <CutoverRunProgress
          runId={budget_run_id}
          onRunningChange={(isRunning) => {
            onRunningChange?.(isRunning);
            onChange?.('budget_run_in_progress', isRunning);
          }}
        />
      )}

      <AlertList
        header="Picking this up later"
        color="info"
        listItems={[
          'The budget run keeps going if you close this, or if you lose the page. Open the assistant again and it brings you straight back to its progress.',
          'For the earlier steps there is nothing saved: start the assistant again with the same replacements and cutover date, and each step reports what is already done.',
          'Anything left behind - a budget on a plan that was not carried across, for instance - is picked up the same way.',
        ]}
      />
    </div>
  );
};

export default CutoverSummary;
